import type React from 'react'
import { useCallback, useMemo, useState } from 'react'

import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import { format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import type { RestrictedWordLog } from '@/modules/restricted-words/types.ts'
import { getLocalDateTime } from '@/utils/date.ts'

import { BlockedMessageDetails } from './dialogs/BlockedMessageDetails'

interface Props {
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  data: RestrictedWordLog[]
  pageCount?: number
}

const getColumns = ({
  viewDetails,
}: {
  viewDetails: (blockedMessageLog: RestrictedWordLog) => void
}): ColumnDef<RestrictedWordLog>[] => [
  {
    header: 'Creator',
    accessorKey: 'creatorId',
    size: 250,
    cell: ({ row }) => {
      const { creatorName, creatorProfilePicture } = row.original

      return (
        <div className="flex flex-shrink-0 items-center">
          <CreatorAvatar
            className="h-[32px] w-[32px] flex-shrink-0"
            name={creatorName}
            avatarSrc={creatorProfilePicture}
          />
          <div className="ml-2.5 font-medium text-title">{creatorName}</div>
        </div>
      )
    },
  },
  {
    header: 'Member',
    accessorKey: 'senderId',
    cell: ({ row }) => {
      const { senderName, senderProfilePicture } = row.original

      return (
        <div className="flex items-center">
          <CreatorAvatar
            className="h-[32px] w-[32px] shrink-0"
            name={senderName}
            avatarSrc={senderProfilePicture}
          />
          <div className="ml-2.5 font-medium text-title">{senderName}</div>
        </div>
      )
    },
  },
  {
    header: 'Blacklisted Word',
    accessorKey: 'restrictedWordId',
    size: 300,
    cell: ({ row }) => {
      const { restrictedWord } = row.original

      return (
        <div
          title={restrictedWord}
          className="max-w-[272px] overflow-hidden overflow-ellipsis whitespace-nowrap font-medium text-title"
        >
          {restrictedWord}
        </div>
      )
    },
  },
  {
    header: 'Fan',
    accessorKey: 'fanName',
    size: 300,
    cell: ({ row }) => {
      const { fanName } = row.original

      return (
        <Tooltip
          content="View Fan Profile"
          asChild
        >
          <a
            href={`https://onlyfans.com/u${row.original.fanId}`}
            target="_blank"
            rel="noreferrer"
          >
            <span className="text-left  font-medium text-primary-500">
              {fanName}
            </span>
          </a>
        </Tooltip>
      )
    },
  },
  {
    header: 'Message',
    accessorKey: 'message',
    cell: ({ row }) => {
      const { message } = row.original

      return (
        <div
          title={message}
          dangerouslySetInnerHTML={{ __html: message }}
          className="max-w-[272px] overflow-hidden overflow-ellipsis whitespace-nowrap font-medium text-title"
        />
      )
    },
  },
  {
    header: 'Date',
    accessorKey: 'createdAt',
    size: 400,
    cell: ({ row }) => {
      const { createdAt } = row.original
      const localDate = getLocalDateTime(createdAt)

      return (
        <div className="flex items-center font-medium text-title">
          <span className="whitespace-nowrap">
            {format(localDate, 'MMM dd, yyyy')}
          </span>
          <div className="mx-2 h-1.5 w-1.5 rounded-full bg-secondary-500 dark:bg-white" />
          <span className="whitespace-nowrap">
            {format(localDate, 'hh:mm aaa')}
          </span>
        </div>
      )
    },
  },
  {
    header: 'Actions',
    accessorKey: 'createdAt',
    cell: ({ row }) => {
      return (
        <Button
          onClick={() => viewDetails(row.original)}
          variant="ghost"
        >
          View More {'>'}
        </Button>
      )
    },
  },
]

export const RestrictedWordsLogsTable = ({
  pagination,
  setPagination,
  data,
  pageCount,
}: Props) => {
  const [selectedBlockedMessage, setSelectedBlockedMessage] =
    useState<RestrictedWordLog | null>(null)

  const handleViewDetails = useCallback(
    (blockedMessageLog: RestrictedWordLog) => {
      setSelectedBlockedMessage(blockedMessageLog)
    },
    [],
  )

  const columns = useMemo(
    () => getColumns({ viewDetails: handleViewDetails }),
    [handleViewDetails],
  )

  return (
    <>
      <DataTable
        paginationState={pagination}
        onPaginationChange={setPagination}
        enablePagination
        data={data}
        columns={columns}
        pageCount={pageCount}
      />
      <BlockedMessageDetails
        onClose={() => setSelectedBlockedMessage(null)}
        blockedMessageLog={selectedBlockedMessage}
      />
    </>
  )
}
